/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar from 'components/toolbar';
import PaginationApp from 'components/pagination';
import RowAction from 'components/rowAction';
import Value from 'components/value';
import { useActions } from 'common/hooks/useActions';
import ModalApp from 'components/modalApp';
import FloorCreateForm from '../create/lib/form';
import QuestionModal from 'components/modal/question';

import * as S from './styles';
import * as T from 'common/types';

interface IProps {
  menuList: { list: T.Pages.FloorListItem[]; totalPages: number };
  loading: boolean;
}

/*
 * Inline rather than two `<ReactSVG>` fetches per row painted `#8A93AC` and
 * `var(--danger)` with a `beforeInjection` callback. `components/rowAction`
 * already gives them the design's quiet-until-hovered colour, so the delete
 * icon no longer has to shout red down the whole column to be findable.
 */
const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
);

const FloorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-5 9 5-9 5-9-5z" />
    <path d="M3 15l9 5 9-5" />
  </svg>
);

export default function FloorsTable({ menuList, loading }: IProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState<T.Pages.FloorListItem | null>();
  const [editData, setEditData] = useState<T.Pages.FloorListItem | null>();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: ''
  });
  const { floorsRequestAction, floorDeleteRequestAction } = useActions();

  const fetchData = () => {
    floorsRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleShowEdit = (item: T.Pages.FloorListItem) => {
    setShowEditModal(true);
    setEditData(item);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditData(null);
  };

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangeTake = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };

  const handleFilter = (field: string, val: string | number) => {
    setMeta({ ...meta, page: 1, [field]: val });
  };

  const handleShowDeleteModal = (data: T.Pages.FloorListItem) => {
    setShowDeleteModal(true);
    setDeleteData(data);
  };

  const handleHideDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteData(null);
  };

  const handleDeleteFloor = () => {
    floorDeleteRequestAction(deleteData?.id as number, () => {
      setShowDeleteModal(false);
      fetchData();
    });
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox onChange={(e) => handleFilter('search', e)} placeholder="Search floors" value={meta.search} />
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 84 },
            { id: 3, key: 'title', title: 'Title', dir: 'left' },
            { id: 7, key: 'edit', title: '', type: 'actions', width: 96 }
          ]}
          style={{ minWidth: 'auto' }}
          data={menuList?.list}
          loading={loading}
          empty={
            meta.search ? (
              <Empty
                title={`No floors match “${meta.search}”`}
                text="Check the spelling, or clear the search to see every floor."
              />
            ) : (
              <Empty
                icon={<FloorIcon />}
                title="No floors yet"
                text="Floors are what rooms, tea boys and orders are grouped by. Add the first one to get started."
              />
            )
          }
          altanfeethiFields={{
            id: (item: T.Pages.FloorListItem) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),
            title: (item: T.Pages.FloorListItem) => (
              <S.Div className="text-left captal">
                <Value>{item?.name}</Value>
              </S.Div>
            ),
            edit: (item: T.Pages.FloorListItem) => (
              <>
                <RowAction onClick={() => handleShowEdit(item)} label={`Edit floor ${item?.name}`}>
                  <PenIcon />
                </RowAction>
                <RowAction onClick={() => handleShowDeleteModal(item)} label={`Delete floor ${item?.name}`}>
                  <TrashIcon />
                </RowAction>
              </>
            )
          }}
        />
      </PanelBoxLayout>
      {menuList?.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * menuList.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={menuList.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangeTake}
        />
      ) : (
        <></>
      )}

      {/*
       * The dialog carries its name in ModalApp's own title rule now — the
       * centred stack with a 40px green disc above a 16px heading was a second
       * heading system that existed only inside these two modals.
       */}
      <ModalApp
        show={showEditModal}
        fixed
        handleClose={handleCloseEdit}
        title={`Edit floor ${editData?.name ?? ''}`.trim()}
        style={{ maxWidth: 400 }}
      >
        <FloorCreateForm data={editData as T.Pages.FloorListItem} closeModal={handleCloseEdit} fetchData={fetchData} />
      </ModalApp>

      <QuestionModal
        handleClose={handleHideDeleteModal}
        show={showDeleteModal}
        model="delete"
        handleSuccess={handleDeleteFloor}
        title={`Delete floor ${deleteData?.name ?? ''}?`.trim()}
        text="Rooms and tea boy assignments on this floor lose it. This cannot be undone."
        successButtonText="Delete floor"
      />
    </S.Content>
  );
}
